// Updated: 2026-08-03 22:10:00 CDT
// Purpose: Validate, upload, parse, and persist owner-scoped screenplay workflow data.

import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Upload, FileText, CheckCircle, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Character } from '@/types/screenplay';
import type { Json } from "@/integrations/supabase/types";
import { importScreenplayFile } from "@/utils/importScreenplayFile";
import { type SpeechLanguage } from '@/utils/speechLanguage';
import {
  MAX_SCREENPLAY_FILE_BYTES,
  MAX_SCREENPLAY_BLOCKS,
  screenplayExtension,
  validateParsedScreenplaySize,
  validateScreenplayFile,
} from "@/utils/screenplayLimits";

export interface ProcessedScreenplay {
  screenplayId: string;
  filename: string;
  characters: Character[];
  parseWarnings: string[];
  speechLanguage?: SpeechLanguage;
}

interface FileUploadProps {
  onFileProcessed: (screenplay: ProcessedScreenplay) => void;
  userId: string;
  beforeUpload?: () => Promise<boolean>;
  defaultSpeechLanguage?: SpeechLanguage;
}

const FileUpload = ({ onFileProcessed, userId, beforeUpload, defaultSpeechLanguage = 'auto' }: FileUploadProps) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const processing=useRef(false);
  const mounted=useRef(true),activeImport=useRef<AbortController|null>(null);
  useEffect(()=>{mounted.current=true;return()=>{mounted.current=false;activeImport.current?.abort();};},[]);
  const [progress,setProgress]=useState('');
  const [uploadError, setUploadError] = useState<{ filename: string; message: string } | null>(null);
  const showUploadError = (filename: string, message: string) => {
    if (!mounted.current) return;
    setUploadError({ filename, message });
    toast.error(message);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    if(processing.current)return;
    
    const files = Array.from(e.dataTransfer.files);
    const screenplayFile = files.find(
      (file) => validateScreenplayFile(file.name, file.size) === null,
    );
    
    if (screenplayFile) {
      setUploadedFile(screenplayFile);
      void processFile(screenplayFile);
    } else if (files[0]) {
      showUploadError(files[0].name,
        validateScreenplayFile(files[0].name, files[0].size) ||
          "Choose one supported screenplay file.",
      );
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if(processing.current)return;
    const file = e.target.files?.[0];
    e.target.value='';
    if (file) {
      setUploadedFile(file);
      void processFile(file);
    }
  };

  const processFile = async (file: File) => {
    if(processing.current)return;
    processing.current=true;
    setUploadError(null);
    const controller=new AbortController(),signal=controller.signal;activeImport.current=controller;
    setIsProcessing(true);
    setProgress('Reading screenplay…');
    let uploadedStoragePath: string | null = null;
    let persisted=false;

    try {
      if (beforeUpload && !(await beforeUpload())) {setUploadedFile(null);return;}
      signal.throwIfAborted();
      const validationMessage = validateScreenplayFile(file.name, file.size);
      if (validationMessage) throw new Error(validationMessage);

      const fileExt = screenplayExtension(file.name)!;
      // Reject unreadable/over-limit PDFs before uploading or creating a row.
      const parseResult = await importScreenplayFile(file,message=>{if(mounted.current&&!signal.aborted)setProgress(message);},signal);
      signal.throwIfAborted();
      const characters = parseResult.characters;

      if (characters.length === 0) {
        throw new Error(
          "No characters or narration could be extracted from this screenplay.",
        );
      }

      const parsedSequence = parseResult.sequence;
      const parsedSizeError = validateParsedScreenplaySize(parsedSequence);
      if (parsedSizeError) throw new Error(parsedSizeError);
      const parseWarnings = parseResult.warnings;
      const title = file.name.replace(/\.[^.]+$/, "").trim() || file.name;
      setProgress('Saving screenplay…');
      const fileName = `${userId}/${Date.now()}-${crypto.randomUUID()}.${fileExt}`;
      const {error:uploadError}=await supabase.storage.from('screenplay-files').upload(fileName,file);
      if(uploadError)throw uploadError;
      uploadedStoragePath=fileName;
      signal.throwIfAborted();

      const { data: screenplay, error: screenplayError } = await supabase
        .from("screenplays")
        .insert({
          user_id: userId,
          title,
          original_filename: file.name,
          source_storage_path: fileName,
          source_format: fileExt,
          original_size_bytes: file.size,
          status: "ready",
          parsed_sequence: parsedSequence as unknown as Json,
          parsed_characters: characters as unknown as Json,
          parse_warnings: parseWarnings,
          character_assignments: {},
          speech_language: defaultSpeechLanguage === 'auto' ? null : defaultSpeechLanguage,
        })
        .select("id")
        .single();

      if (screenplayError) {
        throw screenplayError;
      }
      persisted=true;
      // An insert already in flight may finish after navigation. Keep that
      // complete saved script, but never replace the user's current workspace.
      if(signal.aborted||!mounted.current)return;

      console.debug("[FileUpload] Screenplay persisted:", {
        screenplayId: screenplay.id,
        storagePath: fileName,
        sourceFormat: fileExt,
        characterCount: characters.length,
        sequenceLength: parsedSequence.length,
        warningCount: parseWarnings.length,
        parserStats: parseResult.stats,
      });

      onFileProcessed({
        speechLanguage: defaultSpeechLanguage,
        screenplayId: screenplay.id,
        filename: file.name,
        characters,
        parseWarnings,
      });
      toast.success("Screenplay uploaded and processed successfully!");
      
    } catch (error) {
      if(!signal.aborted)console.error('[FileUpload] Screenplay processing failed:', {
        error,
        filename: file.name,
        size: file.size,
        uploadedStoragePath,
      });

      if (uploadedStoragePath&&!persisted) {
        try {
          const { error: cleanupError } = await supabase.storage
            .from("screenplay-files")
            .remove([uploadedStoragePath]);

          if (cleanupError) {
            console.error("[FileUpload] Failed to clean up uploaded source:", {
              storagePath: uploadedStoragePath,
              cleanupError,
            });
          } else {
            console.debug("[FileUpload] Cleaned up incomplete upload:", {
              storagePath: uploadedStoragePath,
            });
          }
        } catch (cleanupError) {
          console.error("[FileUpload] Failed to clean up uploaded source:", {
            storagePath: uploadedStoragePath,
            cleanupError,
          });
        }
      }

      if(mounted.current)setUploadedFile(null);
      if(!signal.aborted)showUploadError(file.name,
        error instanceof Error
          ? `Failed to process screenplay: ${error.message}`
          : "Failed to process screenplay file.",
      );
    } finally {
      processing.current=false;
      if(activeImport.current===controller)activeImport.current=null;
      if(mounted.current)setIsProcessing(false);
    }
  };

  return (
    <Card className="w-full max-w-2xl">
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <FileText className="h-5 w-5" />
          <span>Upload Screenplay</span>
        </CardTitle>
        <CardDescription>
          Upload a PDF (.pdf), Final Draft (.fdx), Fountain (.fountain), or text (.txt) screenplay up to 10 MB.
        </CardDescription>
      </CardHeader>
      
      <CardContent>
        {uploadError && (
          <div role="alert" aria-label="Screenplay upload error" className="mb-4 rounded-md border border-destructive/60 bg-destructive/10 p-3 text-sm">
            <p className="font-medium">Couldn’t upload {uploadError.filename}</p>
            <p className="mt-1">{uploadError.message}</p>
          </div>
        )}
        {uploadedFile ? (
          <div className="flex items-center justify-between p-6 bg-primary/10 rounded-lg border border-primary/20">
            <div className="flex items-center space-x-3">
              {isProcessing?<Loader2 className="h-8 w-8 animate-spin text-primary"/>:<CheckCircle className="h-8 w-8 text-primary"/>}
              <div>
                <p className="font-medium">{uploadedFile.name}</p>
                <p className="text-sm text-muted-foreground">
                  {(uploadedFile.size / 1024 / 1024).toFixed(2)} MB
                </p>
                {isProcessing&&<p role="status" className="text-sm text-muted-foreground">{progress}</p>}
              </div>
            </div>
            <Button 
              variant="outline" 
              size="sm"
              disabled={isProcessing}
              onClick={() => {
                setUploadedFile(null);
                const input = document.getElementById('file-input') as HTMLInputElement;
                if (input) input.value = '';
              }}
            >
              Remove
            </Button>
          </div>
        ) : (
          <div
            className={cn(
              "border-2 border-dashed rounded-lg p-8 text-center transition-all duration-200",
              isDragOver 
                ? "border-primary bg-primary/5" 
                : "border-border hover:border-primary/50 hover:bg-muted/30"
            )}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <Upload className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-lg font-medium mb-2">Drop your screenplay here</p>
            <p className="text-sm text-muted-foreground mb-4">
              or click to browse files
            </p>
            
            <Button asChild>
              <label htmlFor="file-input" className="cursor-pointer">
                Choose File
              </label>
            </Button>
            
            <input
              id="file-input"
              type="file"
              accept=".pdf,.fdx,.fountain,.txt"
              disabled={isProcessing}
              max={MAX_SCREENPLAY_FILE_BYTES}
              onChange={handleFileInput}
              className="hidden"
            />
            
            <div className="mt-4 text-xs text-muted-foreground">
              Supports: PDF, Final Draft, Fountain, or text · Maximum 10 MB · {MAX_SCREENPLAY_BLOCKS.toLocaleString()} blocks
            </div>
            <p className="mt-2 text-xs text-muted-foreground">PDFs need selectable text (up to 500 pages). Scanned pages require OCR first. Review the imported layout before assigning voices.</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default FileUpload;
